/**
 * A coach's profile, and the one place a coach edits their own.
 *
 * The portal used to have a second editor writing the same row with
 * different rules, which is how "Miami, United States, United States"
 * happened. Now the portal opens this. Done lives in the header and is
 * the only save; it stays off until something changed. Errors land next
 * to the thing that failed, and a toast says when the save took.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Camera, Upload as UploadIcon, Globe, ChevronRight } from "lucide-react";
import { MessageCoachButton } from "../chat/MessageCoachButton";
import { toJpeg, joinLocation, splitLocation } from "../../utils/imageResize";
import { avatarSrc } from "../utils/avatar";
import { GOLD, getAC, getCardBorder } from "../constants/theme";
import { Ic } from "../constants/icons";
import { MEMBERS } from "../utils/community";
import { supabase } from "../../supabaseClient";
import { formatRelativeTime, fetchLeaderboard } from "../utils/communityApi";
import { CertBadge } from "../components/CertBadge";
import { BamCoachBadge } from "../components/BamCoachBadge";
import { InternBadge } from "../components/InternBadge";
import { internCard } from "../interns/internApi";
import { STAGE_LABEL } from "../interns/internLogic";
import { useAdmin } from "../shared/AdminContext";
import { bamToast } from "../shared/dialog";

const COUNTRY_FLAGS = {
  "united states": "🇺🇸", "usa": "🇺🇸", "us": "🇺🇸",
  "germany": "🇩🇪", "deutschland": "🇩🇪",
  "spain": "🇪🇸", "españa": "🇪🇸",
  "australia": "🇦🇺",
  "brazil": "🇧🇷", "brasil": "🇧🇷",
  "india": "🇮🇳",
  "france": "🇫🇷",
  "united kingdom": "🇬🇧", "uk": "🇬🇧", "england": "🇬🇧", "scotland": "🇬🇧",
  "canada": "🇨🇦",
  "estonia": "🇪🇪",
  "finland": "🇫🇮",
  "italy": "🇮🇹", "italia": "🇮🇹",
  "portugal": "🇵🇹",
  "netherlands": "🇳🇱", "holland": "🇳🇱",
  "ireland": "🇮🇪",
  "mexico": "🇲🇽", "méxico": "🇲🇽",
  "argentina": "🇦🇷",
  "japan": "🇯🇵",
  "china": "🇨🇳",
  "south korea": "🇰🇷", "korea": "🇰🇷",
  "philippines": "🇵🇭",
  "new zealand": "🇳🇿",
  "south africa": "🇿🇦",
};
const countryToFlag = (c) => (c ? COUNTRY_FLAGS[c.trim().toLowerCase()] || "" : "");
// The list the country field suggests from. Free text still works.
const COUNTRY_NAMES = ["United States", "Canada", "Mexico", "United Kingdom", "Ireland", "Spain", "Portugal", "France", "Germany", "Netherlands", "Italy", "Estonia", "Finland", "Australia", "New Zealand", "Brazil", "Argentina", "India", "Japan", "China", "South Korea", "Philippines", "South Africa"];

const SELF_COLS = "id, first_name, last_name, handle, role, org, website, avatar_url, banner_url, city, country, bio, ig, x, is_bam_coach, cert_status";

const EMPTY = { first_name: "", last_name: "", handle: "", role: "", org: "", website: "", city: "", country: "", bio: "", ig: "", x: "" };
// City and country come in cleaned, so a row that still holds
// "Miami, United States" in city edits as Miami and United States.
const fromRow = (r) => {
  const d = Object.fromEntries(Object.keys(EMPTY).map(k => [k, r?.[k] || ""]));
  const loc = splitLocation([r?.city, r?.country].filter(Boolean).join(", "));
  d.city = loc.city || ""; d.country = loc.country || "";
  return d;
};
const clean = (s) => String(s || "").trim();
const handleOf = (s) => clean(s).replace(/^@/, "");
const hostOf = (url) => { try { return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, ""); } catch { return url; } };

export function ProfilePanel({ name, dark, C, onClose, onOpenPost, onSaved, go }) {
  const seededM = MEMBERS[name];
  const ac = getAC(name);
  const { isAdmin: viewerIsAdmin } = useAdmin();

  const [loading, setLoading] = useState(true);
  const [row, setRow] = useState(null);
  const [intern, setIntern] = useState(null);
  const [isMe, setIsMe] = useState(false);
  const [stats, setStats] = useState({ posts: 0, month: 0, rank: null });
  const [posts, setPosts] = useState([]);
  const [showAllPosts, setShowAllPosts] = useState(false);

  // Editing
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [orig, setOrig] = useState(EMPTY);
  const [avatar, setAvatar] = useState(null);
  const [banner, setBanner] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const avatarRef = useRef(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        let match = null, me = false;
        // Own row straight from profiles (RLS lets you read yourself, and
        // it carries org and website, which the directory doesn't).
        if (user) {
          const { data: mine } = await supabase.from("profiles").select(SELF_COLS).eq("id", user.id).maybeSingle();
          const full = `${mine?.first_name || ""} ${mine?.last_name || ""}`.trim().toLowerCase();
          if (mine && full === String(name || "").trim().toLowerCase()) { match = mine; me = true; }
        }
        if (!match) {
          const { data: profiles } = await supabase.rpc("fetch_directory_members", { p_limit: 500 });
          match = (profiles || []).find(p => `${p.first_name || ""} ${p.last_name || ""}`.trim().toLowerCase() === String(name || "").trim().toLowerCase()) || null;
        }
        if (!alive || !match) return;
        setRow(match); setIsMe(me);
        internCard(match.id).then(c => { if (alive) setIntern(c); });
        const d = fromRow(match); setDraft(d); setOrig(d);
        if (match.avatar_url) setAvatar(avatarSrc(match.avatar_url, 160));
        if (match.banner_url) setBanner(match.banner_url);

        const since = new Date(Date.now() - 30 * 86400000).toISOString();
        const [{ count: month }, { data: rows, count: all }] = await Promise.all([
          supabase.from("community_posts").select("*", { count: "exact", head: true }).eq("author_id", match.id).is("deleted_at", null).gte("created_at", since),
          supabase.from("community_posts").select("id, content, tag, likes, created_at", { count: "exact" }).eq("author_id", match.id).is("deleted_at", null).order("created_at", { ascending: false }).limit(20),
        ]);
        if (!alive) return;
        setStats(s => ({ ...s, posts: all || 0, month: month || 0 }));
        setPosts((rows || []).map(p => ({ id: p.id, tag: p.tag || "post", content: p.content || "", likes: p.likes || 0, time: formatRelativeTime(p.created_at) })));
        fetchLeaderboard({ limit: 100 }).then(lb => { const i = lb.findIndex(r => r.id === match.id); if (alive && i >= 0) setStats(s => ({ ...s, rank: i + 1 })); }).catch(() => {});
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [name]);

  const m = seededM || {
    initials: (((row?.first_name || "")[0] || "") + ((row?.last_name || "")[0] || "")).toUpperCase() || (name || "?").split(/\s+/).map(s => s[0] || "").join("").toUpperCase().slice(0, 2),
    bio: row?.bio || "", role: row?.role || "", org: row?.org || "", ig: row?.ig || "", tw: row?.x || "", website: row?.website || "",
    loc: joinLocation(row?.city, row?.country), flag: "", photo: avatarSrc(row?.avatar_url, 160) || null,
  };
  const badges = { is_bam_coach: !!row?.is_bam_coach, cert_status: row?.cert_status || "not_started" };

  const dirty = useMemo(() => !!avatarFile || !!bannerFile || Object.keys(EMPTY).some(k => clean(draft[k]) !== clean(orig[k])), [draft, orig, avatarFile, bannerFile]);
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const pickImage = async (e, kind) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    const file = await toJpeg(picked, kind === "avatar" ? 1024 : 1800);
    const url = URL.createObjectURL(file);
    if (kind === "avatar") { setAvatarFile(file); setAvatar(url); }
    else { setBannerFile(file); setBanner(url); }
    setErrors(er => ({ ...er, [kind]: null }));
  };

  const startEdit = () => { setDraft(orig); setErrors({}); setEditing(true); };
  const cancelEdit = () => {
    setDraft(orig); setErrors({});
    setAvatarFile(null); setBannerFile(null);
    setAvatar(avatarSrc(row?.avatar_url, 160) || null); setBanner(row?.banner_url || null);
    setEditing(false);
  };

  const uploadImage = async (file, path) => {
    const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true, contentType: file.type || "image/jpeg" });
    if (error) throw new Error(error.message || "storage error");
    const { data } = supabase.storage.from("avatars").getPublicUrl(path);
    if (!data?.publicUrl) throw new Error("uploaded, but no public link came back");
    return `${data.publicUrl}?v=${Date.now()}`;
  };

  const save = async () => {
    if (saving || !isMe || !dirty) return;
    const errs = {};
    if (!clean(draft.first_name)) errs.first_name = "First name is required.";
    if (!clean(draft.last_name)) errs.last_name = "Last name is required.";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true); setErrors({});
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const updates = {
        first_name: clean(draft.first_name), last_name: clean(draft.last_name),
        handle: handleOf(draft.handle) || null, role: clean(draft.role) || null, org: clean(draft.org) || null,
        website: clean(draft.website) || null, city: clean(draft.city) || null, country: clean(draft.country) || null,
        bio: clean(draft.bio) || null, ig: handleOf(draft.ig) || null, x: handleOf(draft.x) || null,
      };
      const imgErrs = {};
      if (avatarFile) { try { updates.avatar_url = await uploadImage(avatarFile, `${user.id}/avatar.jpg`); } catch (e) { imgErrs.avatar = `Photo didn't upload (${e.message}). Everything else saved.`; } }
      if (bannerFile) { try { updates.banner_url = await uploadImage(bannerFile, `${user.id}/banner.jpg`); } catch (e) { imgErrs.banner = `Banner didn't upload (${e.message}). Everything else saved.`; } }
      // The map pin follows the location. Best effort: a geocoder hiccup
      // never blocks the save, and the nightly backfill catches stragglers.
      const locChanged = clean(draft.city) !== clean(orig.city) || clean(draft.country) !== clean(orig.country);
      if (locChanged && (updates.city || updates.country)) {
        try {
          const q = encodeURIComponent([updates.city, updates.country].filter(Boolean).join(", "));
          const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`, { headers: { "Accept-Language": "en" } });
          const hits = r.ok ? await r.json() : [];
          if (Array.isArray(hits) && hits[0]) { updates.latitude = parseFloat(hits[0].lat); updates.longitude = parseFloat(hits[0].lon); }
        } catch {}
      }
      const { error } = await supabase.from("profiles").update(updates).eq("id", user.id);
      if (error) throw error;
      setRow(r => ({ ...(r || {}), ...updates }));
      setOrig(fromRow({ ...row, ...updates }));
      if (updates.avatar_url) setAvatarFile(null);
      if (updates.banner_url) setBannerFile(null);
      onSaved?.({
        name: `${updates.first_name} ${updates.last_name}`.trim(), role: updates.role || "", org: updates.org || "",
        location: joinLocation(updates.city, updates.country), bio: updates.bio || "", ig: updates.ig || "", x: updates.x || "",
        website: updates.website || "", handle: updates.handle || "", photo: updates.avatar_url || undefined,
      });
      if (Object.keys(imgErrs).length) { setErrors(imgErrs); bamToast("Saved, but a photo didn't upload.", { tone: "warn" }); }
      else { bamToast("Profile saved"); setEditing(false); }
    } catch (e) {
      setErrors({ form: e?.message || "Couldn't save. Try again." });
    } finally { setSaving(false); }
  };

  // ── Styles ──
  const line = dark ? "rgba(255,255,255,0.06)" : "#E8E8E8";
  const dim = dark ? "#8A8A8A" : "#888";
  const mute = dark ? "#666" : "#aaa";
  const text = dark ? "#F2F2F2" : "#111";
  const card = dark ? "#131313" : "#F6F6F6";
  const inp = { width: "100%", background: dark ? "rgba(255,255,255,0.06)" : "#F4F4F4", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "#E0E0E0"}`, borderRadius: 8, padding: "9px 12px", fontSize: 14, color: text, outline: "none", fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box" };
  const label = { fontSize: 10, fontWeight: 700, color: mute, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5 };
  const Err = ({ k }) => errors[k] ? <div style={{ fontSize: 12, color: "#E06060", marginTop: 5, lineHeight: 1.4 }}>{errors[k]}</div> : null;
  const chip = { display: "inline-flex", alignItems: "center", gap: 6, height: 34, padding: "0 12px", borderRadius: 17, fontSize: 11.5, fontWeight: 700, color: "#fff", background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,0.22)", whiteSpace: "nowrap", cursor: "pointer" };

  const contributor = stats.month >= 3 ? "elite" : stats.month >= 1 ? "contrib" : null;
  const igHandle = editing ? null : (m.ig || row?.ig);
  const twHandle = editing ? null : (m.tw || row?.x);
  const site = editing ? null : (m.website || row?.website);
  const bioPrompts = [["What I'm working on", "Working on: "], ["Who I coach", "I coach: "]];
  const addPrompt = (prefix) => { if (draft.bio.includes(prefix)) return; set("bio", (draft.bio.trim() ? draft.bio.trim() + "\n" : "") + prefix); };
  const visiblePosts = showAllPosts ? posts : posts.slice(0, 3);

  return (
    <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: "min(480px, 100vw)", zIndex: 5000, background: dark ? "#0E0E0E" : "#FFFFFF", borderLeft: `1px solid ${line}`, boxShadow: dark ? "-20px 0 60px rgba(0,0,0,0.6)" : "-12px 0 40px rgba(0,0,0,0.12)", display: "flex", flexDirection: "column", fontFamily: "'DM Sans', sans-serif", animation: "slideR .28s cubic-bezier(.22,1,.36,1)" }}>
      <style>{`@keyframes profSkelPulse { 0%,100% { opacity: .5; } 50% { opacity: 1; } }`}</style>

      {/* Banner */}
      <div style={{ height: 130, position: "relative", flexShrink: 0 }}>
        <div style={{ position: "absolute", inset: 0, overflow: "hidden", background: banner ? `url(${banner}) center/cover` : (dark ? "#131313" : "#E8E8E8") }} />
        {!banner && <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${GOLD}22 0%, transparent 60%)` }} />}
        <input ref={bannerRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => pickImage(e, "banner")} />
        <input ref={avatarRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => pickImage(e, "avatar")} />
        <div style={{ position: "absolute", top: 12, right: 12, display: "flex", alignItems: "center", gap: 8 }}>
          {isMe && editing && (
            <div className="btn btn-inline" onClick={() => bannerRef.current?.click()} title="Change banner" style={chip}><UploadIcon size={13} /> Banner</div>
          )}
          {isMe && !editing && (
            <div className="btn btn-inline" onClick={startEdit} style={chip}>Edit profile</div>
          )}
          {isMe && editing && (
            <div className="btn btn-inline" onClick={save} aria-disabled={!dirty || saving}
              style={{ ...chip, padding: "0 14px", fontWeight: 800, color: dirty ? "#111" : "rgba(255,255,255,0.55)", background: dirty ? GOLD : "rgba(0,0,0,0.5)", border: `1px solid ${dirty ? GOLD : "rgba(255,255,255,0.22)"}`, cursor: dirty && !saving ? "pointer" : "default", opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : "Done"}
            </div>
          )}
          <div className="btn btn-inline" onClick={editing ? cancelEdit : onClose} aria-label={editing ? "Cancel" : "Close"} title={editing ? "Cancel" : "Close"}
            style={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,0.22)", cursor: "pointer" }}>
            <X size={14} color="#fff" />
          </div>
        </div>
        {/* Avatar hangs off the banner's bottom edge. The body below pads
            for it so nothing else ever sits under the photo. */}
        <div style={{ position: "absolute", bottom: -34, left: 22 }}>
          <div onClick={isMe && editing ? () => avatarRef.current?.click() : undefined}
            style={{ width: 76, height: 76, borderRadius: "50%", background: avatar ? "transparent" : ac, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 23, fontWeight: 800, color: "#fff", border: `3px solid ${dark ? "#0E0E0E" : "#fff"}`, boxShadow: `0 4px 18px ${ac}55`, overflow: "hidden", cursor: isMe && editing ? "pointer" : "default", position: "relative" }}>
            {avatar ? <img src={avatar} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="" /> : <span>{m.initials}</span>}
          </div>
          {isMe && editing && (
            <div className="btn btn-inline" onClick={() => avatarRef.current?.click()} title="Change photo" aria-label="Change photo"
              style={{ position: "absolute", right: -2, bottom: 0, width: 28, height: 28, borderRadius: "50%", background: GOLD, border: `2px solid ${dark ? "#0E0E0E" : "#fff"}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <Camera size={13} color="#111" />
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: "46px 24px 28px" }}>
        {(errors.avatar || errors.banner) && <div style={{ fontSize: 12, color: "#E06060", marginBottom: 10, lineHeight: 1.4 }}>{errors.avatar || errors.banner}</div>}

        {/* Name row: always rendered, so the stats never climb under the photo */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            {editing ? (
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div><input value={draft.first_name} onChange={e => set("first_name", e.target.value)} placeholder="First name" style={{ ...inp, fontWeight: 800 }} /><Err k="first_name" /></div>
                  <div><input value={draft.last_name} onChange={e => set("last_name", e.target.value)} placeholder="Last name" style={{ ...inp, fontWeight: 800 }} /><Err k="last_name" /></div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <input value={draft.role} onChange={e => set("role", e.target.value)} placeholder="Role, e.g. Skills trainer" style={inp} />
                  <input value={draft.org} onChange={e => set("org", e.target.value)} placeholder="Program or club" style={inp} />
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 19, fontWeight: 800, color: text }}>{name}</div>
                  {badges.is_bam_coach ? <BamCoachBadge size="md" /> : <CertBadge status={badges.cert_status} size="md" />}
                  <InternBadge userId={row?.id} size="md"/>
                </div>
                {row?.handle && <div style={{ fontSize: 12.5, color: GOLD, fontWeight: 600, marginBottom: 3 }}>@{handleOf(row.handle)}</div>}
                {(m.role || m.org) && <div style={{ fontSize: 13, color: dim, marginBottom: 3, fontWeight: 500 }}>{[m.role, m.org].filter(Boolean).join(" · ")}</div>}
                {m.loc && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: dim }}>
                    <span style={{ fontSize: 15 }}>{m.flag || countryToFlag(row?.country)}</span><span>{m.loc}</span>
                  </div>
                )}
              </>
            )}
          </div>
          {isMe && viewerIsAdmin && !editing && <div style={{ fontSize: 11, fontWeight: 800, color: dark ? GOLD : "#111", background: dark ? "rgba(226,221,159,0.12)" : GOLD, border: `1px solid ${dark ? GOLD + "45" : "transparent"}`, borderRadius: 6, padding: "5px 12px", letterSpacing: .8, flexShrink: 0 }}>ADMIN</div>}
        </div>

        {!isMe && row?.id && !editing && (
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <MessageCoachButton userId={row.id} go={go} onOpened={onClose}/>
          </div>
        )}

        {/* Stats. Rank opens the leaderboard. */}
        <div style={{ display: "flex", marginBottom: 14, background: card, borderRadius: 12, overflow: "hidden", border: `1px solid ${line}` }}>
          {[["Posts", loading ? "…" : stats.posts, null], ["This month", loading ? "…" : stats.month, null], ["Rank", stats.rank ? `#${stats.rank}` : "–", go ? () => { onClose?.(); go("community"); } : null]].map(([l, v, onTap], i, a) => (
            <div key={l} onClick={onTap || undefined} className={onTap ? "btn btn-inline" : undefined} title={onTap ? "Open the leaderboard" : undefined}
              style={{ flex: 1, textAlign: "center", padding: "12px 0", borderRight: i < a.length - 1 ? `1px solid ${line}` : "none", cursor: onTap ? "pointer" : "default" }}>
              <div style={{ fontSize: 21, fontWeight: 800, color: dark ? GOLD : "#111" }}>{v}</div>
              <div style={{ fontSize: 11, color: mute, marginTop: 2, display: "inline-flex", alignItems: "center", gap: 2 }}>{l}{onTap && <ChevronRight size={11} />}</div>
            </div>
          ))}
        </div>

        {/* An intern's internship, from their enrollment: where, who leads
            them, what stage. Hours, feedback and evaluations never show here. */}
        {!editing && intern && (
          <div style={{ background: card, border: `1px solid ${line}`, borderRadius: 12, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: mute, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>Internship</div>
            {[["Location", intern.location], ["Lead", intern.lead_name], ["Stage", STAGE_LABEL[intern.stage]], ["Started", intern.started_on ? new Date(`${intern.started_on}T00:00:00`).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : null]]
              .filter(([, v]) => v).map(([l, v]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "3px 0" }}>
                  <span style={{ color: mute }}>{l}</span><span style={{ color: text, fontWeight: 600, textAlign: "right" }}>{v}</span>
                </div>
              ))}
            {intern.lead_id && !isMe && (
              <div style={{ marginTop: 10 }}><MessageCoachButton userId={intern.lead_id} go={go} onOpened={onClose} label={`Message ${String(intern.lead_name || "their lead").split(" ")[0]}`}/></div>
            )}
          </div>
        )}

        {/* Badges, each with the one line that says how you get it. */}
        {!editing && (badges.is_bam_coach || badges.cert_status === "certified" || contributor) && (
          <div style={{ display: "grid", gap: 6, marginBottom: 16 }}>
            {badges.is_bam_coach && <BadgeLine dark={dark} icon="🏀" label="BAM Coach" desc="On the By Any Means staff." />}
            {!badges.is_bam_coach && badges.cert_status === "certified" && <BadgeLine dark={dark} icon="🎓" label="BAM Certified" desc="Finished the coaching certification." />}
            {contributor === "elite" && <BadgeLine dark={dark} icon="🔥" label="Elite Contributor" desc="Three or more posts in the last 30 days." />}
            {contributor === "contrib" && <BadgeLine dark={dark} icon="📹" label="BAM Contributor" desc="Posted in the last 30 days. Three posts makes it Elite." />}
          </div>
        )}

        {/* Bio */}
        {editing ? (
          <div style={{ marginBottom: 14 }}>
            <div style={label}>Bio</div>
            <textarea value={draft.bio} onChange={e => set("bio", e.target.value)} rows={4}
              placeholder="Two lines other coaches can use: what you're working on, and who you coach."
              style={{ ...inp, resize: "vertical", lineHeight: 1.5 }} />
            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              {bioPrompts.map(([t, p]) => (
                <button key={p} type="button" className="btn btn-inline" onClick={() => addPrompt(p)}
                  style={{ fontSize: 11.5, fontWeight: 700, color: GOLD, background: `${GOLD}12`, border: `1px solid ${GOLD}40`, borderRadius: 999, padding: "5px 11px", cursor: "pointer", fontFamily: "inherit" }}>+ {t}</button>
              ))}
            </div>
          </div>
        ) : (m.bio || row?.bio) ? (
          <div style={{ fontSize: 13.5, color: dark ? "#B8B8B8" : "#555", lineHeight: 1.7, marginBottom: 16, whiteSpace: "pre-line" }}>{m.bio || row?.bio}</div>
        ) : isMe ? (
          <div className="btn btn-inline" onClick={startEdit} style={{ fontSize: 13, color: dim, marginBottom: 16, cursor: "pointer", padding: "10px 12px", borderRadius: 10, border: `1px dashed ${line}` }}>Add a bio: what you're working on, and who you coach.</div>
        ) : null}

        {/* Location, two fields. City is one word; country is a list. */}
        {editing && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
            <div><div style={label}>City</div><input value={draft.city} onChange={e => set("city", e.target.value)} placeholder="Miami" style={inp} /></div>
            <div>
              <div style={label}>Country</div>
              <input value={draft.country} onChange={e => set("country", e.target.value)} placeholder="United States" list="bam-countries" style={inp} />
              <datalist id="bam-countries">{COUNTRY_NAMES.map(c => <option key={c} value={c} />)}</datalist>
            </div>
          </div>
        )}

        {/* Socials. Read: icon chips. Edit: three short rows with the icon as the label. */}
        {editing ? (
          <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
            <div style={label}>Links</div>
            {[["ig", <Ic.IG c={dim} s={14} />, "Instagram username"], ["x", <Ic.TW c={dim} s={13} />, "X username"], ["website", <Globe size={14} color={dim} />, "Website"], ["handle", <span style={{ fontSize: 13, fontWeight: 800, color: dim }}>@</span>, "Handle on BAM"]].map(([k, icon, ph]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 22, display: "flex", justifyContent: "center", flexShrink: 0 }}>{icon}</span>
                <input value={draft[k]} onChange={e => set(k, e.target.value)} placeholder={ph} style={inp} />
              </div>
            ))}
          </div>
        ) : (igHandle || twHandle || site) ? (
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {igHandle && <SocialChip dark={dark} href={`https://instagram.com/${handleOf(igHandle)}`} icon={<Ic.IG c={dim} s={13} />} text={`@${handleOf(igHandle)}`} />}
            {twHandle && <SocialChip dark={dark} href={`https://x.com/${handleOf(twHandle)}`} icon={<Ic.TW c={dim} s={12} />} text={`@${handleOf(twHandle)}`} />}
            {site && <SocialChip dark={dark} href={/^https?:\/\//i.test(site) ? site : `https://${site}`} icon={<Globe size={13} color={dim} />} text={hostOf(site)} />}
          </div>
        ) : null}

        {errors.form && <div style={{ fontSize: 12.5, color: "#E06060", marginBottom: 14, lineHeight: 1.4 }}>{errors.form}</div>}

        {/* Recent posts */}
        {!editing && (
          <>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", borderBottom: `1px solid ${line}`, marginBottom: 12, paddingBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: GOLD }}>Recent posts{loading ? "" : ` · ${stats.posts}`}</div>
              {posts.length > 3 && (
                <button type="button" className="btn btn-inline" onClick={() => setShowAllPosts(s => !s)} style={{ background: "transparent", border: "none", color: dim, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
                  {showAllPosts ? "Fewer" : `All ${posts.length}`}
                </button>
              )}
            </div>
            {loading ? (
              <div aria-label="Loading profile">
                {[72, 56, 64].map((w, i) => <div key={i} style={{ background: dark ? "rgba(255,255,255,0.05)" : "#EFEFEF", borderRadius: 10, height: 64, width: `${w}%`, marginBottom: 10, animation: "profSkelPulse 1.3s ease-in-out infinite", animationDelay: `${i * 0.15}s` }} />)}
              </div>
            ) : posts.length === 0 ? (
              <div style={{ textAlign: "center", padding: "28px 0", color: mute, fontSize: 13 }}>{isMe ? "Nothing posted yet. Your first one goes here." : "No posts yet."}</div>
            ) : visiblePosts.map(p => (
              <div key={p.id} className={onOpenPost ? "btn btn-inline" : undefined} onClick={onOpenPost ? () => onOpenPost(p.id) : undefined} title={onOpenPost ? "Open in the feed" : undefined}
                style={{ background: card, border: `1px solid ${line}`, borderLeft: `3px solid ${getCardBorder(p.id)}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10, cursor: onOpenPost ? "pointer" : "default" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#111", background: GOLD, padding: "2px 9px", borderRadius: 10 }}>{p.tag}</div>
                  <div style={{ fontSize: 11, color: mute }}>{p.time}</div>
                  <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: mute }}><Ic.Heart c={mute} s={11} />{p.likes}</span>
                </div>
                <div style={{ fontSize: 13, color: dark ? "#B0B0B0" : "#444", lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.content}</div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function BadgeLine({ dark, icon, label, desc }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10, background: `${GOLD}0d`, border: `1px solid ${GOLD}33` }}>
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span style={{ fontSize: 12.5, fontWeight: 800, color: GOLD, whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ fontSize: 12, color: dark ? "#8A8A8A" : "#777", lineHeight: 1.35 }}>{desc}</span>
    </div>
  );
}

function SocialChip({ dark, href, icon, text }) {
  return (
    <a href={href} target="_blank" rel="noreferrer"
      style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: dark ? "#999" : "#555", background: dark ? "#151515" : "#F0F0F0", padding: "7px 12px", borderRadius: 999, fontWeight: 600, border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "#E8E8E8"}`, textDecoration: "none" }}>
      {icon}{text}
    </a>
  );
}

export default ProfilePanel;
